/**
 * MODAL: CREATE USER
 * Modal pour créer un nouvel utilisateur (superadmin uniquement)
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { CreateUserInput } from '../../schemas/user';
import { createUserSchema } from '../../schemas/user';
import { USER_ROLES, USER_ROLE_LABELS } from '../../types/user';
import { Form, FormInput, FormSelect } from '../ui';
import { Modal, ModalFooter } from '../ui/modal';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserInput) => Promise<void>;
}

export function CreateUserModal({ isOpen, onClose, onSubmit }: CreateUserModalProps) {
  const form = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      role: 'user',
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleFormSubmit = async (data: CreateUserInput) => {
    await onSubmit(data);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Créer un utilisateur"
      size="md"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer"
          isSubmitting={form.formState.isSubmitting}
          formId="create-user-form"
        />
      }
    >
      <Form {...form}>
        <form id="create-user-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            <FormInput
              control={form.control}
              name="full_name"
              label="Nom complet"
              required
              placeholder="Ex: Jean Dupont"
            />

            <FormInput
              control={form.control}
              name="email"
              label="Email"
              type="email"
              required
              placeholder="Ex: jean.dupont@desertours.com"
            />

            <FormInput
              control={form.control}
              name="password"
              label="Mot de passe"
              type="password"
              required
              placeholder="Minimum 8 caractères"
            />

            <FormSelect
              control={form.control}
              name="role"
              label="Rôle"
              required
              options={USER_ROLES.map((role) => ({
                value: role,
                label: USER_ROLE_LABELS[role],
              }))}
            />
          </div>
        </form>
      </Form>
    </Modal>
  );
}
